import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { APIParams, APIResult, callAPI } from '../../callApi';
import { createAsyncActionResource } from '../util';
import {
  ApiActionType,
  EndPointBuilder,
  HTTPMethod,
  ItemBodyMerger,
  ItemBodyParser,
  ItemSummaryBodyMerger,
  KeyComparer,
  LoadPayload,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  QueryParamSelector,
} from './types';
import { buildRequestBody, createErrorHandler, handleBodyLoaded } from './util';

type SuccessPayload = {
  frameSeqNumber: number;
  response: APIResult;
};

export function createLoadAction<
  TItemKey,
  TItemSummary,
  TItemBody,
  TItemBodyAPI,
>(
  displayName: string,
  getEndpoint: EndPointBuilder<TItemKey, TItemSummary, TItemBody>,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  parseItemBody: ItemBodyParser<TItemBody, TItemBodyAPI>,
  mergeItemBody: ItemBodyMerger<TItemBody>,
  mergeSummaryBody: ItemSummaryBodyMerger<TItemSummary, TItemBody>,
  queryParamSelector: QueryParamSelector<TItemBody>,
) {
  const loadAsync = createAsyncActionResource<
    LoadPayload<TItemKey, TItemBody>,
    SuccessPayload
  >(displayName, 'load');

  function* loadRequestSaga(
    params: ActionType<typeof loadAsync.request>,
  ): Generator {
    try {
      const { key, queryParams, bodyFragment } = params.payload;
      const { frameSeqNumber, currentRecord } = (yield select(
        selector,
      )) as MainResourceState<TItemKey, TItemSummary, TItemBody>;

      let lockNum: number | undefined;
      let seqNumFrom: number | undefined;
      if (currentRecord && sameKey(currentRecord.summary, key)) {
        lockNum = currentRecord.loadedLockNum;
        seqNumFrom = currentRecord.seqNumFrom;
      }

      const bodyGET = yield* buildRequestBody(selector, bodyFragment);
      const apiParams: APIParams = {
        queryParams: {
          ...(queryParamSelector(currentRecord?.body) ?? {}),
          ...queryParams,
        },
        body: { bodyGET },
        lockNum,
        seqNumFrom,
      };
      const endPoint = getEndpoint(key);

      const response = yield* call(
        callAPI,
        endPoint,
        'GET' as HTTPMethod,
        apiParams,
      );

      yield put(
        loadAsync.success({
          frameSeqNumber,
          response,
        }),
      );
    } catch (error) {
      yield put(loadAsync.failure(error));
    }
  }

  const loadRequestHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof loadAsync.request
  > = (state, action) => {
    return {
      ...state,
      pendingLoads: state.pendingLoads + 1,
      error: undefined,
      patchData: undefined,
      lastLoadParams: action.payload,
      frameSeqNumber: state.frameSeqNumber + 1,
    };
  };

  const loadSuccessHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof loadAsync.success
  > = (state, action) => {
    return handleBodyLoaded(
      state,
      action.payload.response,
      action.payload.frameSeqNumber,
      false,
      false,
      state.lastLoadParams?.allowMerge ?? true,
      sameKey,
      mergeItemBody,
      mergeSummaryBody,
      parseItemBody,
      ApiActionType.load,
    );
  };

  const loadFailureHandler = createErrorHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof loadAsync.failure
  >(false);

  return {
    loadAsync,
    loadRequestSaga,
    loadRequestHandler,
    loadSuccessHandler,
    loadFailureHandler,
  };
}
